import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../components/UserContext';
import Kategorier from '../components/Kategorier';
import FeaturesSection from '../components/Features_home';
import CtaSection from '../components/CTA';
import DemoBook from '../components/DemoBok';
import BlaHome from '../components/Bla_home';
import TestimonialsSection from '../components/Testimonials';
import { StoryForm } from '../components/StoryForm/StoryForm';
import { Sparkles } from 'lucide-react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../api/apiClient';
import { trackEvent } from '../utils/pixel';
import '../App.css';

const Home = () => {
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const { currentUser, isPrelaunch } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (window.fbq) {
      trackEvent('PageView');
    }
  }, []);
    

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      toast.error('Du måste godkänna villkoren för att fortsätta', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await api.post('/api/prelaunch-signup', { email });
      setSubmitSuccess(true);
      setEmail('');
    } catch (error) {
      console.error('Error submitting email:', error);
      const status = error.response?.status;
      if (status === 409) {
        toast.info('Denna e-postadress är redan registrerad', {
          position: 'top-center',
          autoClose: 3000,
        });
      } else {
        toast.error(error.response?.data?.error || 'Något gick fel', {
          position: 'top-center',
          autoClose: 3000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hantera klick på CTA-knappen
  const handleCTAclick = () => {
    if (!currentUser) {
      toast.info('Du måste vara inloggadd för att skapa en bok', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }
    navigate('/skapa');
  };

  if (showForm) {
    return <StoryForm />;
  }

  return (
    <div className="bg-warmWhite min-h-screen mt-[-80px]">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center h-[700px] md:h-[900px]"
        style={{ backgroundImage: 'url(/images/epicbg.jpg)' }}
      >
        <section className="flex flex-col justify-center items-center text-center px-4 bg-black bg-opacity-50 h-full">
          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8 text-white">
            <div className="animate-fade-up">
              <span className="inline-flex items-center px-3 py-1 md:px-4 md:py-2 rounded-full bg-magic-200/50 text-magic-800 font-quicksand mb-3 md:mb-4 text-sm md:text-base">
                <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                Magiska berättelser med AI
              </span>
              <h1 className="text-3xl md:text-6xl font-bold font-quicksand leading-tight mb-3 md:mb-4 px-2">
                Skapa din egen magiska bok på bara några minuter!
              </h1>
              <p className="text-base md:text-xl font-open-sans max-w-2xl mx-auto px-4">
                Barnboken öppnar dörren till en värld av magi och äventyr där ditt barn är huvudpersonen!
              </p>
            </div>

            <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
              {isPrelaunch ? (
                <div className="bg-transparent p-4 sm:p-6 rounded-lg max-w-md mx-auto w-4/5 sm:w-full">
                  {submitSuccess ? (
                    <div className="text-white font-semibold text-lg">
                      Tack! Du har en rykande färsk kod i din inbox.
                    </div>
                  ) : (
                    <>
                      <h3 className="text-lg sm:text-2xl font-bold mb-3">
                        Få 20% rabatt vid lansering!
                      </h3>
                      <form onSubmit={handleSubmit} className="space-y-3">
                        <input
                          type="email"
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder="Din e-postadress"
                          required
                          className="w-full px-4 py-2 rounded text-gray-800 focus:outline-none focus:ring-2 focus:ring-turquoise"
                        />
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            id="terms"
                            checked={acceptTerms}
                            onChange={e => setAcceptTerms(e.target.checked)}
                            className="mt-1 mr-2"
                          />
                          <label
                            htmlFor="terms"
                            className="text-xs sm:text-sm text-white-300 text-left"
                          >
                            Jag godkänner att min e-postadress sparas för att få rabattkod och
                            information om lanseringen.
                            <a href="/villkor" className="underline ml-1">
                              Läs villkoren här
                            </a>
                            .
                          </label>
                        </div>
                        <button
                          type="submit"
                          disabled={isSubmitting || !acceptTerms}
                          className={`bg-turquoise text-white font-semibold px-6 py-3 rounded-full w-full flex items-center justify-center transition-all ${
                            !acceptTerms || isSubmitting
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-turquoise-dark'
                          }`}
                        >
                          {isSubmitting ? 'Skickar...' : (
                            <>
                              <Sparkles className="w-5 h-5 mr-2" />
                              Få rabattkod
                            </>
                          )}
                        </button>
                      </form>
                      <div className="text-center mt-4 text-2xl text-gray-300">
                        Lansering: <span className="font-bold">30:e april 2024</span>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={handleCTAclick}
                  className="bg-turquoise hover:bg-turquoise-dark w-4/5 sm:w-2/5 text-white font-semibold px-6 py-3 sm:px-8 sm:py-4 rounded-full transform transition-all hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center mx-auto"
                >
                  <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                  Skapa din första bok nu!
                </button>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* Övriga sektioner */}
      <Kategorier />
      <BlaHome />
      <DemoBook />
      <FeaturesSection />
      <CtaSection />
      <TestimonialsSection />

      {/* Toast-container */}
      <ToastContainer />
    </div>
  );
};

export default Home;
